import React, { useState, useEffect, Fragment, useMemo, useRef } from 'react';
import { Modal, Form, Space, Button, Typography, Row, Col, Steps, Tour } from 'antd';
import { UserOutlined, AppstoreOutlined, DatabaseOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AgentNameInput from './AgentNameInput/AgentNameInput';
import KnowledgeBaseSection from './KnowledgeBaseSection/KnowledgeBaseSection';
import PlatformSection from './PlatformSection/PlatformSection';
import { PLATFORM_MAPPER } from './constants';
import { useFacebookSDK } from '../../../Hooks/useFacebookSDK';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import useMobileFullscreenModalBackHandler from '../../../Hooks/useMobileFullscreenModalBackHandler';
import AdvancedControl from './AdvancedControl/AdvancedControl';
import './AddAgent.css';

const { Step } = Steps;
const { Title } = Typography;

const STEP_VALIDATION_FIELDS = [
    ['name'],
    ['type', 'page'],
    ['knowledgeBase'],
    [] // Advanced controls (no required fields)
];

const KNOWLEDGE_BASE_STEP_INDEX = 2;

const AddAgentModal = ({ onClose, onCreateAgent, mode = 'create', initialData = null, guideOpen = false, onGuideClose }) => {
    const { t } = useTranslation();
    const { loading } = useGlobal();
    const { isDarkMode } = useTheme();
    const [state, setState] = useState('');
    const switchFacebookApp = useFacebookSDK(2);
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [isMobile, setIsMobile] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [submitDisabled, setSubmitDisabled] = useState(mode !== 'edit');
    const [continueDisabled, setContinueDisabled] = useState(true);
    const [tourIndex, setTourIndex] = useState(0);
    const [pagesLoadingForTour, setPagesLoadingForTour] = useState(false);
    const [pagesReadyForTour, setPagesReadyForTour] = useState(false);
    const nameInputRef = useRef(null);
    const instructionsInputRef = useRef(null);
    const platformTypeRef = useRef(null);
    const platformTokenButtonRef = useRef(null);
    const platformPageRef = useRef(null);
    const kbSelectRef = useRef(null);
    const kbCreateFormRef = useRef(null);
    const advAnalysisRef = useRef(null);
    const advConsumptionRef = useRef(null);
    const advProcessingRef = useRef(null);
    const advEscalationRef = useRef(null);
    const submitButtonRef = useRef(null);

    const getDom = (node, selector) => {
        if (node && typeof node.getBoundingClientRect === 'function') return node;
        return selector ? document.querySelector(selector) : null;
    };

    const hasValidationErrors = () => {
        const currentFields = STEP_VALIDATION_FIELDS[currentStep];
        if (!currentFields.length) return false; // For steps with no required fields

        return !!form.getFieldsError(currentFields)
            .filter(({ errors }) => errors.length).length;
    };

    const hasRequiredFields = () => {
        const currentFields = STEP_VALIDATION_FIELDS[currentStep];
        if (!currentFields.length) return true; // For steps with no required fields

        return currentFields.every(field => {
            const value = form.getFieldValue(field);
            switch (field) {
                case 'knowledgeBase':
                    // Handle different formats of knowledgeBase value
                    if (typeof value === 'object') {
                        return value && value.id;
                    } else if (typeof value === 'string') {
                        return value.length > 0;
                    } else if (mode === 'edit' && initialData) {
                        // In edit mode, check if initialData has knowledge base info
                        return !!(initialData.knowledge_base_id ||
                            (initialData.knowledge_base && initialData.knowledge_base.id));
                    }
                    return false;
                case 'type':
                case 'page':
                    return value && value.length > 0;
                default:
                    return value !== undefined && value !== null && value !== '';
            }
        });
    };

    const isButtonDisabled = () => {
        if (currentStep === 3) return false; // Advanced step is always enabled

        switch (currentStep) {
            case 0:
                return !form.getFieldValue('name');
            case KNOWLEDGE_BASE_STEP_INDEX:
                const knowledgeBase = form.getFieldValue('knowledgeBase');
                return !knowledgeBase || !knowledgeBase.id;
            case 1: // Platform step
                const type = form.getFieldValue('type');
                const page = form.getFieldValue('page');
                return !type || !page;
            default:
                return hasValidationErrors() || !hasRequiredFields();
        }
    };

    useEffect(() => {
        const checkIfMobile = () => setIsMobile(window.innerWidth <= 768);
        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    const handleCancel = async () => {
        form.resetFields();
        await switchFacebookApp(1);
        onClose();
    };

    // When this wizard is fullscreen on mobile, consume the system back button to close it first
    useMobileFullscreenModalBackHandler(isMobile, handleCancel);

    // Set initial button state for edit mode
    useEffect(() => {
        if (mode === 'edit') {
            // Define the function inside the effect to avoid dependency issues
            const checkButtonDisabled = () => {
                if (currentStep === 3) return false; // Advanced step is always enabled

                switch (currentStep) {
                    case KNOWLEDGE_BASE_STEP_INDEX:
                        const knowledgeBase = form.getFieldValue('knowledgeBase');
                        return !knowledgeBase || !knowledgeBase.id;
                    case 1: // Platform step
                        const type = form.getFieldValue('type');
                        const page = form.getFieldValue('page');
                        // Handle both normal string values and labelInValue object format
                        return !type || !page || (typeof page === 'object' && !page.value);
                    default:
                        return hasValidationErrors() || !hasRequiredFields();
                }
            };

            setSubmitDisabled(checkButtonDisabled());
        }
    }, [mode, currentStep, form, hasRequiredFields, hasValidationErrors]);

    // Update continueDisabled state when currentStep changes or form values change
    useEffect(() => {
        setContinueDisabled(isButtonDisabled());
    }, [currentStep, form.getFieldsValue()]);

    const handleSubmit = async () => {
        try {
            // Validate all fields regardless of mode
            await form.validateFields();

            const values = form.getFieldsValue(true);

            // Extract page ID from the page object (if using labelInValue)
            const pageId = values.page?.value || values.page;

            // Determine the knowledge base ID based on form values or initial data
            let knowledgeBaseId;

            if (typeof values.knowledgeBase === 'object' && values.knowledgeBase?.id) {
                // Format: { id: "123", name: "KB Name" }
                knowledgeBaseId = values.knowledgeBase.id;
            } else if (typeof values.knowledgeBase === 'string' && values.knowledgeBase) {
                // Format: "123"
                knowledgeBaseId = values.knowledgeBase;
            } else if (mode === 'edit' && initialData?.knowledge_base_id) {
                // Fallback to initialData if available in edit mode
                knowledgeBaseId = initialData.knowledge_base_id;
            } else if (mode === 'edit' && initialData?.knowledge_base?.id) {
                // Alternative object format in initialData
                knowledgeBaseId = initialData.knowledge_base.id;
            }

            // Ensure we have a knowledge base ID for submission
            if (mode === 'edit' && !knowledgeBaseId) {
                throw new Error('Knowledge base ID is required');
            }

            const submissionValues = {
                id: mode === 'edit' ? initialData.id : undefined,
                name: values.name,
                knowledge_base_id: knowledgeBaseId,
                admin_contact_ids: values.admins,
                process_audio: values.analyzeAudio,
                process_document: values.analyzeDocument,
                process_image: values.analyzeImage,
                process_video: values.analyzeVideo,
                allow_escalation: values.allow_escalation,
                system_instruction: values.system_instruction,
                is_consumption_limit_enabled: values.is_consumption_limit_enabled,
                ...(values.is_consumption_limit_enabled ? {
                    max_coins_consumption: values.max_coins_consumption,
                    consumption_duration: values.consumption_duration,
                } : {}),
                snooze_duration: values.snooze_duration,
                is_processing_delay_enabled: values.is_processing_delay_enabled,
                ...(values.is_processing_delay_enabled ? {
                    processing_delay: values.processing_delay,
                    processing_jitter: values.processing_jitter,
                } : {}),
                ...(mode === 'create' ? {
                    platform_account: {
                        platform_type: PLATFORM_MAPPER[values.type],
                        external_platform_account_id: pageId,
                    }
                } : {}),
            };

            setState('submitting');
            onCreateAgent(submissionValues, () => setState(''));
        } catch (error) {
            // Validation failed - handled by form UI
        }
    };

    const steps = [
        {
            title: t('agent_info'),
            content: <AgentNameInput nameRef={nameInputRef} instructionsRef={instructionsInputRef} />
        },
        {
            title: t('platform'),
            content: <PlatformSection
                form={form}
                mode={mode}
                id={initialData?.id}
                initialData={initialData}
                onPagesLoadingChange={(loading) => setPagesLoadingForTour(!!loading)}
                onPagesReadyChange={(ready) => setPagesReadyForTour(!!ready)}
                typeRef={platformTypeRef}
                tokenButtonRef={platformTokenButtonRef}
                pageRef={platformPageRef}
            />
        },
        {
            title: t('knowledge'),
            content: <KnowledgeBaseSection
                initialData={initialData}
                form={form}
                navigate={navigate}
                onUpdate={() => setContinueDisabled(isButtonDisabled())}
                onStartCreate={() => {
                    // Keep the tour on the knowledge base step
                    setCurrentStep(KNOWLEDGE_BASE_STEP_INDEX);
                }}
                onCreated={(kb) => {
                    // If tour is on kb-create, bump to next tour step (submit) or proceed within KB steps
                    setContinueDisabled(isButtonDisabled());
                }}
                onAssigned={() => {
                    // If the tour is currently on KB select, jump directly to Consumption step
                    if (guideOpen && tourIndex === 5) {
                        setTourIndex(7); // 7 = adv_consumption step index
                    }
                }}
            />
        },
        {
            title: t('advanced'),
            content: <AdvancedControl />
        },
    ];

    const tourIndexToModalStep = useMemo(() => (index) => {
        if ([0, 1].includes(index)) return 0; // name, instructions
        if ([2, 3, 4].includes(index)) return 1; // platform
        if ([5].includes(index)) return 2; // knowledge base
        if ([6, 7, 8, 9, 10].includes(index)) return 3; // advanced (incl. submit)
        return 0;
    }, []);

    const baseTourSteps = useMemo(() => ([
        { title: t('agent_tour_name_title'), description: t('agent_tour_name_desc'), target: () => getDom(nameInputRef.current && nameInputRef.current.input, '[data-tour="agent-name"]') },
        { title: t('agent_tour_instructions_title'), description: t('agent_tour_instructions_desc'), target: () => getDom(instructionsInputRef.current && instructionsInputRef.current.resizableTextArea && instructionsInputRef.current.resizableTextArea.textArea, '[data-tour="agent-instructions"]') },
        { title: t('agent_tour_platform_title'), description: t('agent_tour_platform_desc'), target: () => getDom(platformTypeRef.current, '[data-tour="platform-type"]') },
        { title: t('agent_tour_token_title'), description: t('agent_tour_token_desc'), target: () => getDom(platformTokenButtonRef.current, '[data-tour="platform-token"]') },
        { title: t('agent_tour_page_title'), description: t('agent_tour_page_desc'), target: () => getDom(platformPageRef.current, '[data-tour="platform-page"]') },
        { title: t('agent_tour_kb_select_title'), description: t('agent_tour_kb_select_desc'), target: () => getDom(kbSelectRef.current, '[data-tour="kb-select"]') },
        { title: t('agent_tour_adv_analysis_title'), description: t('agent_tour_adv_analysis_desc'), target: () => getDom(advAnalysisRef.current, '[data-tour="adv-analysis"]') },
        { title: t('agent_tour_adv_consumption_title'), description: t('agent_tour_adv_consumption_desc'), target: () => getDom(advConsumptionRef.current, '[data-tour="adv-consumption"]') },
        { title: t('agent_tour_adv_processing_title'), description: t('agent_tour_adv_processing_desc'), target: () => getDom(advProcessingRef.current, '[data-tour="adv-processing"]') },
        { title: t('agent_tour_adv_escalation_title'), description: t('agent_tour_adv_escalation_desc'), target: () => getDom(advEscalationRef.current, '[data-tour="adv-escalation"]') },
        { title: t('agent_tour_submit_title'), description: t('agent_tour_submit_desc'), target: () => getDom(submitButtonRef.current, '[data-tour="wizard-submit"]') },
    ]), [t]);

    const isTourNextDisabled = useMemo(() => (index) => {
        switch (index) {
            case 0: // agent name
                return !form.getFieldValue('name');
            case 2:
                return !form.getFieldValue('type')
            case 3: return !pagesReadyForTour; // allow next once pages are fetched
            case 4: return !form.getFieldValue('page');
            case 5: // KB select
                return !form.getFieldValue(['knowledgeBase', 'id']);
            default:
                return false;
        }
    }, [form, pagesReadyForTour]);

    const tourSteps = useMemo(() => (
        baseTourSteps.map((step, idx) => ({
            ...step,
            nextButtonProps: { disabled: isTourNextDisabled(idx) }
        }))
    ), [baseTourSteps, isTourNextDisabled, form.getFieldsValue()]);

    useEffect(() => {
        if (guideOpen) {
            setCurrentStep(tourIndexToModalStep(0));
        }
    }, [guideOpen, tourIndexToModalStep]);

    const next = async () => {
        try {
            if (STEP_VALIDATION_FIELDS[currentStep].length > 0) {
                await form.validateFields(STEP_VALIDATION_FIELDS[currentStep]);
            }
            setCurrentStep(current => Math.min(current + 1, steps.length - 1));
        } catch (error) {
            // Validation failed - handled by form UI
        }
    };

    const handleValuesChange = (changedValues) => {
        if (currentStep === 3) {
            setSubmitDisabled(false);
            return;
        }

        if (mode === 'edit') {
            // In edit mode, validate all fields
            form.validateFields()
                .then(() => {
                    setSubmitDisabled(isButtonDisabled());
                    setContinueDisabled(isButtonDisabled());
                })
                .catch(() => {
                    setSubmitDisabled(isButtonDisabled());
                    setContinueDisabled(isButtonDisabled());
                });
            return;
        }

        // Create mode logic (unchanged)
        if ('knowledgeBase' in changedValues || 'type' in changedValues || 'page' in changedValues) {
            form.validateFields(STEP_VALIDATION_FIELDS[currentStep])
                .then(() => {
                    setSubmitDisabled(isButtonDisabled());
                    setContinueDisabled(isButtonDisabled());
                })
                .catch(() => {
                    setSubmitDisabled(true);
                    setContinueDisabled(true);
                });
        } else {
            const buttonDisabled = isButtonDisabled();
            setSubmitDisabled(buttonDisabled);
            setContinueDisabled(buttonDisabled);
        }
    };

    const prev = () => setCurrentStep(current => Math.max(current - 1, 0));

    const onStepClick = async (step) => {
        if (mode === 'edit' || step <= currentStep) {
            setCurrentStep(step);
        }
    };

    return (
        <Modal
            size={isMobile ? 'small' : 'default'}
            title={
                <div style={{ margin: 0 }}>
                    <Title level={4} style={{ margin: '0 0 16px 0' }}>
                        {mode === 'edit' ? t('edit_agent') : t('new_agent')}
                    </Title>
                    <Steps
                        current={currentStep}
                        size="small"
                        onChange={onStepClick}
                        direction="horizontal"
                        responsive={false}
                        className={isMobile ? 'mobile-icon-steps' : ''}
                        style={{
                            marginBottom: 0,
                            position: 'sticky',
                            top: 0,
                            backgroundColor: isDarkMode ? '#1f1f1f' : '#fff',
                            zIndex: 1,
                            padding: '8px 0',
                            paddingBottom: 0,
                        }}
                    >
                        {steps.map((step, index) => (
                            <Step
                                key={index}
                                title={step.title}
                                icon={
                                    index === 0 ? <UserOutlined /> :
                                        index === 1 ? <AppstoreOutlined /> :
                                            index === 2 ? <DatabaseOutlined /> :
                                                <SettingOutlined />
                                }
                                disabled={mode !== 'edit' && index > currentStep}
                            />
                        ))}
                    </Steps>
                </div>
            }
            open
            onCancel={handleCancel}
            maskClosable={false}
            footer={isMobile ? (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', width: '100%' }}>
                    {currentStep > 0 && (
                        <Button onClick={prev} style={{ maxWidth: '120px' }}>
                            {t('back')}
                        </Button>
                    )}
                    {mode === 'edit' ? (
                        <>
                            <Button
                                type="primary"
                                onClick={handleSubmit}
                                loading={state === 'submitting' && loading}
                                disabled={submitDisabled}
                                style={{ 
                                    backgroundColor: '#52c41a',
                                    borderColor: '#52c41a',
                                }}
                                data-tour="wizard-submit"
                            >
                                {t('update_agent')}
                            </Button>
                            {currentStep < steps.length - 1 && (
                            <Button
                                type="primary"
                                onClick={next}
                                style={{ maxWidth: '120px' }}
                            >
                                    {t('next')}
                                </Button>
                            )}
                        </>
                    ) : currentStep < steps.length - 1 ? (
                        <Button
                            type="primary"
                            onClick={next}
                            disabled={continueDisabled}
                            style={{ maxWidth: '160px' }}
                        >
                            {t('continue')}
                        </Button>
                        ) : (
                        <Button
                            type="primary"
                            autoFocus
                            onClick={handleSubmit}
                            disabled={submitDisabled}
                            loading={state === 'submitting' && loading}
                            style={{ maxWidth: '160px' }}
                                data-tour="wizard-submit"
                                ref={submitButtonRef}
                        >
                            {t('create_agent')}
                        </Button>
                    )}
                </div>
            ) : (
                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                    <div>
                        {currentStep > 0 && (
                            <Button onClick={prev} style={{ marginInlineEnd: 8 }}>
                                {t('back')}
                            </Button>
                        )}
                    </div>

                    <div>
                        {mode === 'edit' ? (
                            <>
                                <Button
                                    type="primary"
                                    onClick={handleSubmit}
                                    loading={state === 'submitting' && loading}
                                    disabled={submitDisabled}
                                    style={{ 
                                        backgroundColor: '#52c41a',
                                        borderColor: '#52c41a'
                                    }}
                                    data-tour="wizard-submit"
                                >
                                    {t('update_agent')}
                                </Button>
                                {currentStep < steps.length - 1 && (
                                    <Button
                                        type="primary"
                                        onClick={next}
                                        style={{ marginLeft: 8 }}
                                    >
                                        {t('next')}
                                    </Button>
                                )}
                            </>
                        ) : currentStep < steps.length - 1 ? (
                            <Button
                                type="primary"
                                onClick={next}
                                disabled={continueDisabled}
                            >
                                {t('continue')}
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                autoFocus
                                onClick={handleSubmit}
                                disabled={submitDisabled}
                                loading={state === 'submitting' && loading}
                                data-tour="wizard-submit"
                            >
                                {t('create_agent')}
                            </Button>
                        )}
                    </div>
                </Space>
            )}
            width={isMobile ? "100%" : "900px"}
            style={isMobile ? {
                top: 0,
                margin: 0,
                maxWidth: '100%',
                height: '100vh',
                padding: 0,
            } : {
                maxHeight: '80vh',
                top: '5vh'
            }}
            wrapClassName="custom-modal-wrap"
            styles={{
                header: {
                    padding: '16px 24px',
                    marginBottom: 0,
                    borderBottom: `1px solid ${isDarkMode ? '#434343' : '#f0f0f0'}`,
                    flexShrink: 0,
                    backgroundColor: isDarkMode ? '#1f1f1f' : '#fff',
                },
                body: {
                    height: isMobile ? 'calc(100dvh - 160px)' : '60vh',
                    overflowY: 'auto',
                    padding: isMobile ? '12px 16px 8px 16px' : '16px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    marginTop: 0,
                    flex: 1,
                    minHeight: 0,
                    WebkitOverflowScrolling: 'touch',
                    backgroundColor: isDarkMode ? '#141414' : '#fff',
                },
                footer: {
                    marginTop: 0,
                    paddingTop: 10,
                    paddingBottom: isMobile ? 16 : 10,
                    boxSizing: 'border-box',
                    flexShrink: 0,
                    position: isMobile ? 'sticky' : 'relative',
                    bottom: 0,
                    backgroundColor: isDarkMode ? '#1f1f1f' : '#fff',
                    borderTop: isMobile ? `1px solid ${isDarkMode ? '#434343' : '#f0f0f0'}` : 'none',
                    zIndex: 1000,
                },
                wrapper: {
                    overflowY: isMobile ? 'hidden' : 'auto',
                }
            }}
        >
            <Form
                form={form}
                layout="vertical"
                name="add_agent_form"
                preserve={true}
                initialValues={mode === 'edit' ? {
                    name: initialData.name,
                    system_instruction: initialData.system_instruction,
                    knowledgeBase: initialData.knowledge_base_id
                        ? { id: initialData.knowledge_base_id }
                        : initialData.knowledge_base
                            ? { id: initialData.knowledge_base.id, name: initialData.knowledge_base.name }
                            : undefined,
                    type: Object.keys(PLATFORM_MAPPER)[Object.values(PLATFORM_MAPPER).indexOf(initialData.platform_account.platform_type)],
                    page: initialData.platform_account.external_platform_account_id,
                    analyzeImage: initialData.process_image,
                    analyzeVideo: initialData.process_video,
                    analyzeAudio: initialData.process_audio,
                    analyzeDocument: initialData.process_document,
                    allow_escalation: initialData.allow_escalation,
                    is_consumption_limit_enabled: initialData.is_consumption_limit_enabled,
                    max_coins_consumption: initialData.max_coins_consumption,
                    consumption_duration: initialData.consumption_duration,
                    snooze_duration: initialData.snooze_duration,
                    is_processing_delay_enabled: initialData.is_processing_delay_enabled,
                    processing_delay: initialData.processing_delay,
                    processing_jitter: initialData.processing_jitter,
                    admins: initialData.admin_contacts?.map(admin => admin.id) || [],
                } : {
                    name: '',
                    knowledgeBase: '',
                    type: 'messenger',
                    admins: [],
                    system_instruction: 'You are a customer service AI assistant created by Custom Answer, designed to help customers of this platform account.',
                    analyzeImage: true,
                    analyzeVideo: true,
                    analyzeAudio: true,
                    analyzeDocument: true,
                    allow_escalation: true,
                    max_coins_consumption: 60,
                    consumption_duration: 3600,
                    snooze_duration: 600,
                    processing_delay: 15,
                    processing_jitter: 20,
                }}
                onValuesChange={handleValuesChange}
                style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
                onFieldsChange={(changedFields) => {
                    // Check if knowledgeBase field has changed
                    if (changedFields.some(field => field.name[0] === 'knowledgeBase')) {
                        setSubmitDisabled(isButtonDisabled());
                    }
                }}
            >
                <div style={{ flex: 1 }}>
                    <Row align="bottom"
                        gutter={16}
                        style={{
                            overflowY: 'auto',
                            padding: '8px 0',
                            marginBottom: 16,
                        }}>
                        {steps.map((step, index) => (
                            <Fragment key={index}>
                                {currentStep === index && (
                                    <Col span={24}>
                                        {step.content}
                                    </Col>
                                )}
                            </Fragment>
                        ))}
                    </Row>
                </div>
            </Form>
            <Tour
                open={guideOpen}
                steps={tourSteps}
                current={tourIndex}
                zIndex={1100}
                rootClassName="add-agent-tour"
                mask={{ style: { zIndex: 1099, pointerEvents: 'none' } }}
                onClose={() => {
                    setTourIndex(0);
                    onGuideClose && onGuideClose();
                }}
                onChange={(next) => {
                    setCurrentStep(tourIndexToModalStep(next));
                    setTourIndex(next);
                    // Ensure escalation setting is visible by scrolling to it when that step is active
                    if (next === 9) {
                        setTimeout(() => {
                            const el = document.querySelector('[data-tour="adv-escalation"]');
                            if (el && typeof el.scrollIntoView === 'function') {
                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                        }, 0);
                    }
                }}
            />
        </Modal>
    );
};

export default AddAgentModal;
